import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
  Box,
  CardMedia,
  Grid,
  Link,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  utcToLocalDateLong,
  utcToLocalDateTime,
} from '@par/shared/utils/date-time';
import {
  CATEGORY_SETS_LABEL,
  MODE_ABBR,
  MODE_DISPLAY_NAME,
} from '../common/constants';
import {
  Achievement,
  DbQuestionData,
  DetailedPlayer,
  GameDb,
  Player,
} from '../components/room/types';
import { assignRanks } from '../components/room/utils';
import { fetchGet } from '../api/common';
import { useNavigate } from 'react-router-dom';
import { ScoreCardModal } from '../modals/ScoreCardModal';
import { StyledPaperItem } from '../common/mui-components';
import { BiGhost } from 'react-icons/bi';
import { UserAchievement } from '@par/shared/common/types';
import { Profile } from '@par/shared/types/player';

type PropTypes = {
  achievement: Achievement;
  close: () => void;
  open: boolean;
  viewProfile: Profile;
};

export const ProfileAchievementsDetailsDialog = ({
  achievement,
  close,
  open,
  viewProfile,
}: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [games, setGames] = useState<GameDb[]>();
  const [gameRow, setGameRow] = useState<GameDb>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [achievedDate, setAchievedDate] = useState<string>();
  const [gamesCount, setGamesCount] = useState<number>();
  const [pageNum, setPageNum] = useState(1);
  const [userAchievement, setUserAchievement] = useState<UserAchievement>();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    if (!open) return;
    fetchUserAchievement();
    fetchAchievementGamesCount();
    fetchAchievementGames();
  }, [open]);

  useEffect(() => {
    getAchievedDate();
  }, [userAchievement]);

  useEffect(() => {
    const offset = 10 * pageNum - 10;
    const limit = 10;
    fetchAchievementGamesCount();
    fetchAchievementGames(limit, offset);
  }, [pageNum]);

  const fetchUserAchievement = async () => {
    const res = await fetchGet(
      `/achievements/user-achievement/${viewProfile.id}/${achievement.name}`
    );
    setUserAchievement(res);
  };

  const fetchAchievementGamesCount = async () => {
    const res = await fetchGet(
      `/achievements/user/gameIds/count/${viewProfile.id}/${achievement.name}`
    );
    setGamesCount(res.count);
  };

  const fetchAchievementGames = async (limit = 10, offset = 0) => {
    const res = await fetchGet(
      `/achievements/user/gameIds/${viewProfile.id}/${achievement.name}/${limit}/${offset}`
    );
    setGames(res);
  };

  const handlePaginateOnChange = (event: any, pageNum: number) => {
    setPageNum(pageNum);
  };

  const getAchievedDate = () => {
    if (!userAchievement || !userAchievement.updatedAt) {
      setAchievedDate('not yet achieved');
      return;
    }
    setAchievedDate(utcToLocalDateLong(userAchievement.updatedAt));
  };

  const getGameScores = (data: GameDb) => {
    const players = Object.keys(data.players);
    if (!players.includes(viewProfile.id)) return 'DNP';
    if (
      data &&
      data.players &&
      (data.mode === 'solo' || data.mode === 'sit-n-go')
    ) {
      const playerMe = data.players[viewProfile.id];
      const myScore = playerMe?.parScore;
      if (myScore === undefined) return '';
      return `${myScore > 0 ? '+' + myScore : myScore}`;
    }
    if (data.mode !== 'solo') {
      const playerMe = data.players[viewProfile.id];
      const myScore = playerMe?.parScore;
      if (!myScore === undefined) return '';
      const playerThemProfileId = Object.keys(data.players).find(
        (profileId) => profileId !== viewProfile.id
      );
      const playerThem = playerThemProfileId
        ? data.players[playerThemProfileId]
        : undefined;
      const theirScore = playerThem?.parScore;
      if (theirScore === undefined) return '';
      return `${myScore > 0 ? '+' + myScore : myScore} to ${
        theirScore > 0 ? '+' + theirScore : theirScore
      }`;
    }
  };

  const results = (game: GameDb) => (
    <Stack>
      <Typography variant="body2">
        {utcToLocalDateTime(game.createdAt)}
      </Typography>
      <Link
        sx={{ cursor: 'pointer' }}
        onClick={() => handleViewResultsClick(game)}
      >
        View Results
      </Link>
    </Stack>
  );

  const handleViewResultsClick = async (game: GameDb) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    setGameRow(res.game);
    setQuestionSet(res.questionSet);
    setOpenScorecardModal(true);
  };

  const stackName = (r: GameDb) => (
    <Tooltip placement="top" title={`Game ID: ${r.gameId}`}>
      <Stack>
        <Stack direction="row" spacing={1}>
          {r.ranked ? (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: theme.palette.ranked.main,
              }}
            >
              Ranked
            </Typography>
          ) : null}
          {r.ghostChallenge ? (
            <BiGhost
              style={{
                fontSize: '18px',
                color: theme.palette.warning.main,
              }}
            />
          ) : null}
        </Stack>
        {r.customGameName || 'N/A'}
      </Stack>
    </Tooltip>
  );

  const handleUsernameClick = async (e: any, player: Player) => {
    e.stopPropagation();
    navigate(`/profile/overview/${player.profileId}`);
  };

  const getRatingNextToUsername = (player: Player) => {
    const formatRating = (rating: number) => {
      const rounded = Math.round(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };
    return formatRating(player.rankedRatingDiff || 0);
  };

  const ratingColor = (player: Player) => {
    if (Math.round(player.rankedRatingDiff || 0) > 0)
      return theme.palette.success.main;
    if (Math.round(player.rankedRatingDiff || 0) < 0)
      return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getJoinedPlayers = (game: GameDb) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const rankedPlayers = assignRanks(
      Object.values(game.players).sort((a, b) => a.parScore - b.parScore)
    );
    return (
      <Stack width={'100%'}>
        {rankedPlayers.map((p, i) => (
          <Link
            key={i}
            component="button"
            variant="body2"
            onClick={(e) => handleUsernameClick(e, p)}
            textAlign={'left'}
          >
            <Stack direction={'row'} sx={{ whiteSpace: 'nowrap' }}>
              {game.mode === 'sit-n-go' ? `${p.rank}.` : ''} {p.username}
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: ratingColor(p) }}
                  >
                    {getRatingNextToUsername(p)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Link>
        ))}
      </Stack>
    );
  };

  return (
    <>
      {gameRow && questionSet ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          gameId={gameRow.gameId}
          handicap={gameRow.handicap}
          isDaily={gameRow.daily}
          openScordcardModal={openScorecardModal}
          mode={gameRow.mode}
          players={gameRow.players as Record<string, DetailedPlayer>}
          profileId={viewProfile.id}
          questionSet={questionSet}
          ranked={gameRow.ranked}
        ></ScoreCardModal>
      ) : null}
      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogContent sx={{ width: '100%', height: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              marginTop: 2,
            }}
          >
            <Grid container spacing={2} className="CONTAINER-MAIN">
              <Grid item xs={12} sm={12} className="CONTAINER-TOP">
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    className="IMAGE"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={achievement.imageUrl}
                      alt={achievement.displayName}
                      sx={{
                        objectFit: 'fill',
                        height: 220,
                        width: 220,
                        boxShadow: `
                        inset 0 0 10px #e0e0e0,
                        inset 20px 0 80px #f7ecb2,
                        inset -20px 0 80px #caf3f3,
                        inset 20px 0 300px #e0b8e0,
                        inset -20px 0 300px #f6ef61,
                        0 0 10px #e0e0e0,
                        -5px 0 20px #c0d1bc,
                        5px 0 20px #fffd5f;`,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8} className="TEXT">
                    <StyledPaperItem sx={{ paddingX: 2 }}>
                      <Stack spacing={3} mb={4}>
                        <Typography
                          color={'primary.light'}
                          fontWeight={600}
                          variant="h5"
                          textAlign={'center'}
                          width={'100%'}
                          sx={{
                            textShadow:
                              '0 0 0px #cda68c, 0 0 1px #cda68c, 1px 1px 8px #4c3d34',
                          }}
                        >
                          {achievement.displayName}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          <Typography color={'white'} mb={1}>
                            To achieve:
                          </Typography>{' '}
                          <Typography
                            fontWeight={600}
                            sx={{
                              textShadow: `0 0 4px ${theme.palette.secondary.main}, 0 0 0 ${theme.palette.secondary.main}, 0px 0px 20px ${theme.palette.secondary.dark}`,
                            }}
                          >
                            {achievement.condition}
                          </Typography>
                        </Typography>
                      </Stack>
                      <Stack spacing={1} mb={2}>
                        <Typography fontStyle={'italic'}>
                          "{achievement.quote}"
                        </Typography>
                        <Typography variant="caption" textAlign={'right'}>
                          &mdash; {achievement.quoteBy}
                        </Typography>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} className="CONTAINER-BOTTOM">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} className="GAMES">
                    <StyledPaperItem
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Typography fontWeight={600}>Achieved on</Typography>

                        <Typography
                          sx={{
                            textShadow: `0 0 0px ${theme.palette.primary.main}, 0 0 1px ${theme.palette.primary.main}, 1px 1px 8px ${theme.palette.primary.dark}`,
                          }}
                        >
                          {achievedDate}
                        </Typography>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                  {games && games.length ? (
                    <Grid item xs={12} sm={12} className="GAMES">
                      <StyledPaperItem
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Stack spacing={2} sx={{ mb: 2 }}>
                          <Typography fontWeight={600}>
                            Game(s) achieved in
                          </Typography>
                          {gamesCount ? (
                            <Pagination
                              count={Math.ceil(gamesCount / 10)}
                              size={xsBreakpoint ? 'small' : 'medium'}
                              onChange={handlePaginateOnChange}
                              page={pageNum}
                              sx={{ display: 'flex', justifyContent: 'right' }}
                            />
                          ) : null}
                          <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                              <TableHead>
                                <TableRow>
                                  <TableCell width={'15%'}>Name</TableCell>
                                  <TableCell width={'2%'}>Mode</TableCell>
                                  <TableCell width={'2%'}>
                                    <Tooltip placement="top" title="Difficulty">
                                      <span>Diff.</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell width={'8%'}>Category</TableCell>
                                  <TableCell width={'8%'}>Players</TableCell>
                                  <TableCell
                                    width={'10%'}
                                    style={{ textAlign: 'center' }}
                                  >
                                    Scores
                                  </TableCell>
                                  <TableCell width={'10%'}>Results</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {games?.map((row: GameDb) => (
                                  <TableRow
                                    hover
                                    key={row._id}
                                    // onClick={() => handleRowClick(row)}
                                    // sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell>{stackName(row)}</TableCell>
                                    <TableCell>
                                      <Tooltip
                                        placement="top-end"
                                        title={
                                          MODE_DISPLAY_NAME[row.mode] || ''
                                        }
                                      >
                                        <span>
                                          {row.daily
                                            ? 'Daily'
                                            : MODE_ABBR[row.mode]}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      {row.demo
                                        ? '--'
                                        : row.gameType === 'casual'
                                        ? 'Cas.'
                                        : 'Pro'}
                                    </TableCell>
                                    <TableCell>
                                      {row.demo
                                        ? 'Demo'
                                        : CATEGORY_SETS_LABEL[row.categorySet]}
                                    </TableCell>
                                    <TableCell>
                                      {getJoinedPlayers(row)}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                      {getGameScores(row)}
                                    </TableCell>
                                    <TableCell>{results(row)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Stack>
                      </StyledPaperItem>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
